import React, { useEffect, useState } from 'react';
import PropTypes, { oneOf } from 'prop-types';
import { List } from 'immutable';
import _ from 'lodash';
import t from '../../lib/I18n';
import { DisruptionAlertDaysOptions, DisruptionAlertDurationOptions, WeekdayNames, InitialWeekDayJourney } from '../../constants/AppConstants';
import Search from '../../records/Search';
import Timeband from '../../records/Timeband';
import BaseModal from '../general/BaseModal';
import AddJourneyForm from './AddJourneyForm';
import DisruptionAlertActions from '../../actions/DisruptionAlertActions';
import TrackingActions from '../../actions/TrackingActions';
import FavouriteJourneysService from '../../services/FavouriteJourneysService';
import useStoreState from '../../stores/useStoreState';
import DisruptionAlertStore from '../../stores/DisruptionAlertStore';
import JourneyPlanService from '../../services/JourneyPlanService';
import DisruptionAlert from '../../records/DisruptionAlert';
import User from '../../records/User';
import { legsToDisruptionAlertLegs } from '../../helpers/DisruptionAlert';


const defaultBusinessDays = [
    { id: 1, day: WeekdayNames.MONDAY, checked: true },
    { id: 2, day: WeekdayNames.TUESDAY, checked: true },
    { id: 3, day: WeekdayNames.WEDNESDAY, checked: true },
    { id: 4, day: WeekdayNames.THURSDAY, checked: true },
    { id: 5, day: WeekdayNames.FRIDAY, checked: true },
];

const AddRegularJourneyModal = ({
    editingJourney,
    editingAlert,
    enableRegularJourneyCreation,
    getFavouriteJourneys,
    isEditingAlert,
    mode = 'ocdr',
    prepopulatedSearch,
    presetWeekday,
    remainingJourneys,
    setEditingJourney = _.noop,
    setIsJourneyCardModalOpen = _.noop,
    successCallback,
    user,
}) => {
    const isOCDRMode = mode === 'ocdr';

    const bankHolidays = useStoreState(DisruptionAlertStore, 'bankHolidays');

    const getNewSearch = () => new Search({ numJourneys: 5 });

    const [businessDays, setBusinessDays] = useState(defaultBusinessDays);
    const [weekday, setWeekday] = useState(InitialWeekDayJourney);
    const [search, setSearch] = useState(prepopulatedSearch || getNewSearch());
    const [journeySearchResults, setJourneySearchResults] = useState(getNewSearch());
    const [selectedJourneys, setSelectedJourneys] = useState([]);
    const [saveRegularJourneyAlert, setSaveRegularJourneyAlert] = useState(enableRegularJourneyCreation);
    const [stationFacilityAlert, setStationFacilityAlert] = useState(false);
    const [showErrorWell, setShowErrorWell] = useState(false);
    const [errorWellText, setShowErrorWellText] = useState('');
    const [disabledSaveJourney, setDisabledSaveJourney] = useState(true);
    const [timeAdjusted, setTimeAdjusted] = useState(false);

    const { outward } = journeySearchResults;

    const outwardJourneys = outward && outward
        .map(x => x.journey)
        .filter(x => x.origin.time.scheduledTime.isSame(search.outboundDateTime.datetime, 'day'));

    const routeCode = outward && outward.first() && outward.first().fares && outward.first().fares.singles.first().route.code;

    const originNlc = search && search.origin && search.origin.nlc;
    const destinationNlc = search && search.destination && search.destination.nlc;

    const resetAddJourneyForm = () => {
        setSearch(getNewSearch());
        setJourneySearchResults(getNewSearch());
        setSelectedJourneys([]);
        setBusinessDays(defaultBusinessDays);
        setWeekday(InitialWeekDayJourney);
        setEditingJourney(null);
    };

    const setJourneyTime = datetime => {
        const outboundDateTime = new Timeband({ datetime });
        setSearch(search.set('outboundDateTime', outboundDateTime));
        setTimeAdjusted(true);
    };

    const formatLeg = (leg, ddmFormat) => {
        const origin = leg.origin.first();
        const destination = leg.destination.first();

        if (!origin) {
            return false;
        }

        return {
            origin: ddmFormat ? origin.station : origin.station.nlc,
            destination: ddmFormat ? destination.station : destination.station.nlc,
            departure: origin.getTime(),
            arrival: destination.getTime(),
            transferTime: leg.transferTime,
            mode: leg.serviceDetails.mode,
            toc: leg.serviceDetails.toc && leg.serviceDetails.toc.id,
            seats: [],
            reservable: leg.serviceDetails.reservable,
            facilities: '',
            rsid: leg.rsid,
            tuid: leg.serviceDetails.trainUid,
            isTemporaryTrain: leg.serviceDetails.isTemporaryTrain,
          };
    };

    const formatJourney = (journey, ddmFormat) => {
        if (ddmFormat) {
            return journey.legs.map(x => formatLeg(x, true));
        }
        return ({
            time: journey.getDepartureDateTime().format('HH:mm'),
            legs: journey.legs.map(leg => formatLeg(leg, false))
        });
    };

    const saveFavouriteJourney = async () => {
        setShowErrorWell(false);
        setShowErrorWellText('');
        let hasErrors = false;

        const services = selectedJourneys.map(service => formatJourney(service, false));

        let daysOfWeek = [];

        if (weekday === 'saturday' || weekday === 'sunday') {
            daysOfWeek = [weekday];
        } else {
            daysOfWeek = businessDays.filter(day => day.checked).map(day => day.day);
        }

        const journey = {
            journeyId: editingJourney ? editingJourney.journeyId : null,
            journeyType: weekday,
            origin: search.origin.nlc,
            destination: search.destination.nlc,
            routeCode,
            services,
            daysOfWeek,
        };

        if (isOCDRMode) {
            try {
                if (editingJourney) {
                    await FavouriteJourneysService.editFavouriteJourney(user, journey);
                } else {
                    await FavouriteJourneysService.saveFavouriteJourney(user, journey);
                }
            } catch (e) {
                console.warn('error saving Regular Journey', e);
                setShowErrorWell(true);
                hasErrors = true;
                setShowErrorWellText(t('one-click-delay-repay.error-setting-up-regular-journey', {
                    alert: 'Favourite Journey'
                }));
            }
        }

        if ((saveRegularJourneyAlert || !isOCDRMode) && !hasErrors) {
            let days = DisruptionAlertDaysOptions.WEEKDAYS;

            if (weekday === 'saturday') {
                days = DisruptionAlertDaysOptions.SATURDAYS;
            } else if (weekday === 'sunday') {
                days = DisruptionAlertDaysOptions.SUNDAYS;
            }

            const { origin, destination } = search;

            const wrappedServices = selectedJourneys.map(x => ({ journey: x }));

            const formData = {
                customEndDate: null,
                days,
                destination,
                duration: DisruptionAlertDurationOptions.INFINITE,
                origin,
                selectedServices: new List(wrappedServices),
                startDate: search.outboundDateTime.datetime,
                weekdays: {
                    MONDAY: daysOfWeek.includes('monday'),
                    TUESDAY: daysOfWeek.includes('tuesday'),
                    WEDNESDAY: daysOfWeek.includes('wednesday'),
                    THURSDAY: daysOfWeek.includes('thursday'),
                    FRIDAY: daysOfWeek.includes('friday'),
                }
            };

            if (isEditingAlert) {
                const formattedLegs = selectedJourneys.map(x => formatJourney(x, true));

                const disruptionAlertLegs = formattedLegs.reduce((acc, journeyLegs, index) => {
                    return acc.concat(legsToDisruptionAlertLegs(journeyLegs, index + 1).toArray());
                }, []);

                const updatedAlert = editingAlert
                    .set('origin', search.origin)
                    .set('destination', search.destination)
                    .set('departureTime', disruptionAlertLegs[0].departureTime)
                    .set('legs', disruptionAlertLegs)
                    .set('alertForever', true)
                    .set('alertFrom', null)
                    .set('alertTo', null)
                    .set('daysOfWeek', daysOfWeek.map(day => day.toUpperCase()))
                    .set('isStationAlert', stationFacilityAlert)
                    .set('customer', user);

                await DisruptionAlertActions.update(user, updatedAlert);

                successCallback({ success: true });
            } else {
                try {
                    const alrt = await DisruptionAlertActions.createFromBuilder({ user, formData, stationFacilityAlert });
                    TrackingActions.saveRegularJourneyAlertWithOCDRFavJourney();
                    if (!isOCDRMode) {
                        successCallback({
                            alrt,
                            success: true
                        });
                    }
                } catch (e) {
                    setShowErrorWell(true);
                    hasErrors = true;
                    setShowErrorWellText(t('one-click-delay-repay.error-setting-up-regular-journey', {
                        alert: 'Journey Alert'
                    }));
                    if (!isOCDRMode) {
                        successCallback({ err: e, success: true });
                    }
                }
            }
        }

        if (!hasErrors) {
            if (isOCDRMode) {
                await getFavouriteJourneys(user);
            }
            setIsJourneyCardModalOpen(false);
            resetAddJourneyForm();
        }
    };

    const searchOrigin = search.origin;
    const searchDestination = search.destination;
    const searchDatetime = search.outboundDateTime.datetime;

    useEffect(() => {
        async function searchJourneys() {
            let outboundDateTime = search.outboundDateTime.datetime;

            const isOutboundTravelDay = x => outboundDateTime.isSame(x, 'day');

            let isBankHoliday = bankHolidays.some(isOutboundTravelDay);

            /*
                We need to use a while loop because it is
                possible that there are two bank holiday Mondays in a row
                (this happens in December if Christmas and New Year fall on a Monday)
            */
            while (isBankHoliday) {
                outboundDateTime = outboundDateTime.add(7, 'days');
                isBankHoliday = bankHolidays.some(isOutboundTravelDay);
            }

            const journeys = await JourneyPlanService.search({
                delayRepayFiltering: isOCDRMode,
                search,
                user,
            });

            setJourneySearchResults(journeys);
        }

        if (timeAdjusted && search && search.origin.nlc && search.destination.nlc && search.isValid()) {
            _.defer(searchJourneys);
        }
    // eslint-disable-next-line
    }, [searchOrigin, searchDestination, searchDatetime, presetWeekday]);

    /*
        if user changes stations, we need to remove previously selected journeys
        as only journeys from one set of stations at a time can be saved
    */
    useEffect(() => {
        setSelectedJourneys([]);
    }, [weekday, originNlc, destinationNlc]);

    useEffect(() => {
        _.defer(DisruptionAlertActions.getBankHolidays);

        if (prepopulatedSearch) {
            let initialDay = InitialWeekDayJourney;

            initialDay = prepopulatedSearch.outboundDateTime.datetime.format('dddd').toLowerCase();

            if (presetWeekday) {
                setWeekday(presetWeekday);
            } else if (initialDay !== 'saturday' && initialDay !== 'sunday') {
                setWeekday('weekday');
            } else {
                setWeekday(initialDay);
            }
        }
    }, [prepopulatedSearch, presetWeekday]);

    return (
        <BaseModal
            className="modal--med"
            modalTitleClass="journey-cards-modal-title"
            titleText="auto-dr.account.subscribe.journeys.modal.title"
            continueText={isOCDRMode ? "auto-dr.account.subscribe.journeys.modal.save" : "ddm.account.subscribe.journeys.modal.save"}
            disableContinue={disabledSaveJourney}
            onContinue={saveFavouriteJourney}
            onClose={() => {
                setIsJourneyCardModalOpen(false);
                resetAddJourneyForm();
                setShowErrorWell(false);
                setShowErrorWellText('');
            }}
        >
            <AddJourneyForm
                businessDays={businessDays}
                setBusinessDays={setBusinessDays}
                weekday={weekday}
                setWeekday={setWeekday}
                setJourneyTime={setJourneyTime}
                setDisabledSaveJourney={setDisabledSaveJourney}
                search={search}
                searchErrors={search.getValidationErrors()}
                setSearch={setSearch}
                journeySearchResults={journeySearchResults}
                selectedJourneys={selectedJourneys}
                setSelectedJourneys={setSelectedJourneys}
                outwardJourneys={outwardJourneys}
                remainingJourneys={remainingJourneys}
                editingJourney={editingJourney}
                saveRegularJourneyAlert={saveRegularJourneyAlert}
                setSaveRegularJourneyAlert={setSaveRegularJourneyAlert}
                showErrorWell={showErrorWell}
                errorWellText={errorWellText}
                enableRegularJourneyCreation={enableRegularJourneyCreation}
                setStationFacilityAlert={setStationFacilityAlert}
                stationFacilityAlert={stationFacilityAlert}
                isOCDRMode={isOCDRMode}
            />
        </BaseModal>
    );
};

AddRegularJourneyModal.propTypes = {
    editingAlert: PropTypes.instanceOf(DisruptionAlert),
    editingJourney: PropTypes.string,
    enableRegularJourneyCreation: PropTypes.bool,
    getFavouriteJourneys: PropTypes.func,
    isEditingAlert: PropTypes.bool,
    mode: PropTypes.string,
    prepopulatedSearch: PropTypes.instanceOf(Search),
    presetWeekday: oneOf(['weekday', 'saturday', 'sunday']),
    remainingJourneys: PropTypes.number,
    setEditingJourney: PropTypes.func,
    setIsJourneyCardModalOpen: PropTypes.func,
    successCallback: PropTypes.func,
    user: PropTypes.instanceOf(User),
};

export default AddRegularJourneyModal;
